import type { AssetType, Holding, SlipPlan, ValidatedHolding } from '@porttang/core';
import { useMemo, useState } from 'react';
import { Chevron, useCollapsedGroups } from '../../shared/collapsible';
import { AssetTag, Card, SectionLabel, tintFor } from '../../shared/pixel';
import { SlipScanModal } from '../slips/SlipScanModal';
import { useMarkSlipApplied } from '../slips/useScanSlip';
import { HoldingForm } from './HoldingForm';
import { TradeForm, type TradeResult } from './TradeForm';
import {
  useApplySale,
  useCreateHolding,
  useDeleteHolding,
  useHoldings,
  useUpdateHolding,
} from './useHoldings';

const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  stock: 'หุ้น',
  etf: 'ETF',
  crypto: 'คริปโต',
  cash: 'เงินสด',
};

/** ตัด trailing zero ทิ้งเพื่อให้อ่านง่าย แต่ยังคงความละเอียดของค่าเดิมไว้ครบ */
function formatNumber(decimalString: string): string {
  const asNumber = Number(decimalString);
  if (!Number.isFinite(asNumber)) {
    return decimalString;
  }
  return asNumber.toLocaleString('th-TH', { maximumFractionDigits: 8 });
}

type Editing =
  | { mode: 'closed' }
  | { mode: 'new' }
  | { mode: 'scan' }
  | { mode: 'edit'; holding: Holding }
  | { mode: 'buy'; holding: Holding }
  | { mode: 'sell'; holding: Holding };

const ADD_BUTTON_CLASS =
  'rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink-100 transition hover:brightness-110';

const SCAN_BUTTON_CLASS =
  'rounded-md border border-line-strong px-4 py-2 text-sm text-ink-200 transition hover:border-brand hover:text-brand-text';

export function HoldingsPage() {
  const { data: holdings, isPending, isError, error, refetch } = useHoldings();
  const createMutation = useCreateHolding();
  const updateMutation = useUpdateHolding();
  const deleteMutation = useDeleteHolding();
  const saleMutation = useApplySale();
  const markSlipMutation = useMarkSlipApplied();
  const { isOpen, toggle } = useCollapsedGroups();

  const [editing, setEditing] = useState<Editing>({ mode: 'closed' });

  // รายชื่อแพลตฟอร์มมาจาก holdings ที่มีอยู่ ไม่ได้เก็บแยกไว้ที่ไหน
  const platforms = useMemo(
    () =>
      [...new Set((holdings ?? []).map((item) => item.platform))].sort((a, b) =>
        a.localeCompare(b, 'th'),
      ),
    [holdings],
  );

  // section ละแพลตฟอร์ม — ในแต่ละ section คงลำดับเดิมที่เพิ่มเข้ามา
  const groups = useMemo(
    () =>
      platforms.map((platform) => ({
        platform,
        items: (holdings ?? []).filter((holding) => holding.platform === platform),
      })),
    [platforms, holdings],
  );

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    saleMutation.isPending;
  const writeError =
    createMutation.error ?? updateMutation.error ?? deleteMutation.error ?? saleMutation.error;

  function handleSubmit(validated: ValidatedHolding) {
    if (editing.mode === 'edit') {
      updateMutation.mutate(
        { id: editing.holding.id, holding: validated },
        { onSuccess: () => setEditing({ mode: 'closed' }) },
      );
      return;
    }
    createMutation.mutate(validated, { onSuccess: () => setEditing({ mode: 'closed' }) });
  }

  /**
   * รับผลที่คำนวณแล้วจาก `TradeForm` มาเขียนลง Firestore
   *
   * ตัวเลขใหม่คำนวณเสร็จตั้งแต่ใน modal แล้ว (`@porttang/core`) ที่นี่จึงมีหน้าที่แค่บันทึก
   * — ห้ามคำนวณซ้ำตรงนี้ เพราะจะกลายเป็นสูตรชุดที่สองที่ไม่มี test คุม
   */
  function handleTrade(result: TradeResult) {
    if (editing.mode !== 'buy' && editing.mode !== 'sell') {
      return;
    }

    const { id, ...fields } = editing.holding;
    const close = { onSuccess: () => setEditing({ mode: 'closed' }) };

    // ขาย → แก้/ลบ holding พร้อมจดรายการขายใน batch เดียว (spec Section 5.5)
    if (result.mode === 'sell') {
      saleMutation.mutate({ holdingId: id, plan: result.plan }, close);
      return;
    }

    updateMutation.mutate(
      { id, holding: { ...fields, quantity: result.next.quantity, avgCost: result.next.avgCost } },
      close,
    );
  }

  /**
   * บันทึกผลของสลิปที่ผู้ใช้ยืนยันแล้ว
   *
   * `plan` คำนวณเสร็จมาจาก `@porttang/core` ตั้งแต่ในจอทบทวน — ที่นี่มีหน้าที่แค่เขียน
   * เหตุผลเดียวกับ `handleTrade`: สูตรชุดที่สองที่ไม่มี test คุมคือจุดที่ตัวเลขสองฝั่ง
   * จะเริ่มไม่ตรงกันโดยไม่มีใครสังเกต
   *
   * ลายนิ้วมือสลิปถูกจดหลังเขียนพอร์ตสำเร็จเท่านั้น — จดก่อนแล้วเขียนล้มจะกลายเป็น
   * ใบที่ถูกเตือนว่าซ้ำทั้งที่ยังไม่เคยเข้าพอร์ตจริง
   */
  function handleApplySlip(plan: SlipPlan, slipHash: string | null) {
    const close = {
      onSuccess: () => {
        if (slipHash !== null) {
          markSlipMutation.mutate(slipHash);
        }
        setEditing({ mode: 'closed' });
      },
    };

    if (plan.kind === 'create') {
      createMutation.mutate(plan.holding, close);
      return;
    }

    if (plan.kind === 'blocked') {
      return;
    }

    if (plan.kind === 'reduce') {
      saleMutation.mutate(
        { holdingId: plan.holding.id, plan: { next: plan.next, sale: plan.sale } },
        close,
      );
      return;
    }

    const { id, ...fields } = plan.holding;

    updateMutation.mutate(
      { id, holding: { ...fields, quantity: plan.next.quantity, avgCost: plan.next.avgCost } },
      close,
    );
  }

  function handleDelete(holding: Holding) {
    // ลบแล้วกู้ไม่ได้ จึงต้องถามก่อน — และลบตรง ๆ ไม่จดกำไร ต่างจากการกด "ขาย"
    const confirmed = globalThis.confirm(
      holding.assetType === 'cash'
        ? `ลบ ${holding.symbol} ออกจากพอร์ต?`
        : `ลบ ${holding.symbol} ออกจากพอร์ต?\n\nถ้าขายไปแล้ว ให้กด "ขาย" แทน กำไรที่ได้จะถูกเก็บไว้`,
    );
    if (confirmed) {
      deleteMutation.mutate(holding.id);
    }
  }

  function rowActions(holding: Holding) {
    return (
      <RowActions
        holding={holding}
        onBuy={() => setEditing({ mode: 'buy', holding })}
        onSell={() => setEditing({ mode: 'sell', holding })}
        onEdit={() => setEditing({ mode: 'edit', holding })}
        onDelete={() => handleDelete(holding)}
      />
    );
  }

  return (
    /*
      หน้านี้ไม่กินความกว้างเต็ม 1240px เหมือนแดชบอร์ด
      แดชบอร์ดมีของสองคอลัมน์มาถ่างพื้นที่ แต่หน้านี้มีตารางเดียว 5 คอลัมน์ที่เนื้อหาสั้น ๆ
      ปล่อยเต็มความกว้างแล้วช่องว่างระหว่างคอลัมน์จะกว้างจนสายตาลากจากชื่อไปหาตัวเลขไม่ถึง
    */
    <section className="mx-auto w-full max-w-[980px]">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <SectionLabel>YOUR HOLDINGS</SectionLabel>
          <h1 className="mt-2 text-xl font-semibold text-ink-100">สินทรัพย์ที่ถือ</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditing({ mode: 'scan' })}
            className={SCAN_BUTTON_CLASS}
          >
            สแกนสลิป
          </button>
          <button
            type="button"
            onClick={() => setEditing({ mode: 'new' })}
            className={ADD_BUTTON_CLASS}
          >
            + เพิ่มสินทรัพย์
          </button>
        </div>
      </header>

      {writeError !== null && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-down/25 bg-down/10 px-4 py-3 text-sm text-down"
        >
          บันทึกไม่สำเร็จ: {writeError.message}
        </p>
      )}

      {isPending && <p className="py-12 text-center font-mono text-sm text-ink-500">กำลังโหลด…</p>}

      {isError && (
        <div className="rounded-[14px] border border-down/30 bg-down/5 px-6 py-10 text-center">
          <p className="text-sm text-down">โหลดข้อมูลไม่สำเร็จ: {error.message}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-4 rounded-md border border-line-strong px-4 py-2 text-sm text-ink-200 transition hover:border-brand hover:text-brand-text"
          >
            ลองใหม่
          </button>
        </div>
      )}

      {holdings !== undefined && holdings.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-line-dash px-6 py-16 text-center">
          <SectionLabel>EMPTY PORTFOLIO</SectionLabel>
          <p className="mt-4 text-ink-200">ยังไม่มีสินทรัพย์ในพอร์ต</p>
          <p className="mt-1.5 text-sm text-ink-500">
            เพิ่มสิ่งที่ถืออยู่ พร้อมจำนวนและต้นทุนเฉลี่ย แล้วระบบจะดึงราคามาคำนวณให้
          </p>
          <button
            type="button"
            onClick={() => setEditing({ mode: 'new' })}
            className={`mt-6 ${ADD_BUTTON_CLASS}`}
          >
            + เพิ่มสินทรัพย์แรก
          </button>
        </div>
      )}

      {holdings !== undefined && holdings.length > 0 && (
        <>
          {/*
            จอกว้าง: ตารางเดียว แบ่ง section ต่อแพลตฟอร์มด้วย `<tbody>`
            ถ้าแยกเป็นหลายตาราง ความกว้างคอลัมน์จะคำนวณแยกกัน ตัวเลขข้าม section จะไม่ตรงแนว
            คอลัมน์ "แพลตฟอร์ม" ถูกตัดออก เพราะหัว section บอกไว้แล้ว
          */}
          <Card className="hidden overflow-hidden sm:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line-soft text-left font-mono text-[10.5px] tracking-[0.5px] text-ink-500">
                    <th className="px-5 py-3 font-normal">ASSET</th>
                    <th className="px-5 py-3 font-normal">ประเภท</th>
                    <th className="px-5 py-3 text-right font-normal">จำนวน</th>
                    <th className="px-5 py-3 text-right font-normal">ต้นทุน/หน่วย</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                {groups.map(({ platform, items }) => {
                  const open = isOpen(platform);
                  return (
                    <tbody key={platform}>
                      <tr className="border-y border-line bg-sunken/60 transition hover:bg-hover">
                        <td colSpan={5} className="p-0">
                          <PlatformHeader
                            platform={platform}
                            count={items.length}
                            open={open}
                            onToggle={() => toggle(platform)}
                            className="px-5 py-2.5"
                          />
                        </td>
                      </tr>
                      {open &&
                        items.map((holding) => (
                          <tr
                            key={holding.id}
                            className="border-b border-line-soft text-ink-100 transition last:border-0 hover:bg-hover"
                          >
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <AssetTag symbol={holding.symbol} />
                                <div className="min-w-0">
                                  <div className="font-semibold">{holding.symbol}</div>
                                  <div className="truncate text-[11px] text-ink-500">
                                    {holding.displayName}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-ink-400">
                              {ASSET_TYPE_LABELS[holding.assetType]}
                            </td>
                            <td className="px-5 py-3.5 text-right font-mono text-[13px]">
                              {formatNumber(holding.quantity)}
                            </td>
                            <td className="px-5 py-3.5 text-right font-mono text-[13px]">
                              <UnitCost holding={holding} />
                            </td>
                            <td className="px-5 py-3.5 text-right whitespace-nowrap">
                              {rowActions(holding)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  );
                })}
              </table>
            </div>
          </Card>

          {/* จอเล็ก: การ์ด — ตารางบนมือถือต้องเลื่อนแนวนอนซึ่งใช้ยาก (spec Section 9.3) */}
          <div className="space-y-4 sm:hidden">
            {groups.map(({ platform, items }) => {
              const open = isOpen(platform);
              return (
                <section key={platform}>
                  <PlatformHeader
                    platform={platform}
                    count={items.length}
                    open={open}
                    onToggle={() => toggle(platform)}
                    className="rounded-lg border border-line bg-sunken px-3.5 py-2.5"
                  />
                  {open && (
                    <ul className="mt-2 space-y-3">
                      {items.map((holding) => (
                        <li
                          key={holding.id}
                          className="rounded-[14px] border border-line bg-surface p-4"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <AssetTag symbol={holding.symbol} />
                            <div className="min-w-0">
                              <div className="font-semibold text-ink-100">{holding.symbol}</div>
                              <div className="truncate text-[11px] text-ink-500">
                                {ASSET_TYPE_LABELS[holding.assetType]} · {holding.displayName}
                              </div>
                            </div>
                          </div>
                          <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <dt className="text-xs text-ink-400">จำนวน</dt>
                              <dd className="font-mono text-ink-100">
                                {formatNumber(holding.quantity)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-ink-400">ต้นทุน/หน่วย</dt>
                              <dd className="font-mono text-ink-100">
                                <UnitCost holding={holding} />
                              </dd>
                            </div>
                          </dl>

                          <div className="mt-3 border-t border-line-soft pt-3">
                            {rowActions(holding)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>

          <p className="mt-4 font-mono text-[11px] text-ink-600">
            มูลค่าปัจจุบันและกำไรขาดทุนจะแสดงในหน้าภาพรวม เมื่อระบบดึงราคาได้แล้ว (T1.4)
          </p>
        </>
      )}

      {(editing.mode === 'new' || editing.mode === 'edit') && (
        <HoldingForm
          holding={editing.mode === 'edit' ? editing.holding : null}
          platforms={platforms}
          isSaving={isSaving}
          onCancel={() => setEditing({ mode: 'closed' })}
          onSubmit={handleSubmit}
        />
      )}

      {(editing.mode === 'buy' || editing.mode === 'sell') && (
        <TradeForm
          holding={editing.holding}
          mode={editing.mode}
          isSaving={isSaving}
          onCancel={() => setEditing({ mode: 'closed' })}
          onSubmit={handleTrade}
        />
      )}

      {editing.mode === 'scan' && (
        <SlipScanModal
          holdings={holdings ?? []}
          platforms={platforms}
          isSaving={isSaving}
          onCancel={() => setEditing({ mode: 'closed' })}
          onApply={handleApplySlip}
        />
      )}
    </section>
  );
}

/** หัว section ของแพลตฟอร์ม — กดเพื่อพับ/กางรายการข้างใต้ */
function PlatformHeader({
  platform,
  count,
  open,
  onToggle,
  className,
}: {
  platform: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  className: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className={`flex w-full items-center gap-2.5 text-left ${className}`}
    >
      <Chevron open={open} />
      <span
        aria-hidden
        className="size-2.5 shrink-0"
        style={{ backgroundColor: tintFor(platform) }}
      />
      <span className="min-w-0 truncate font-semibold text-ink-100">{platform}</span>
      <span className="shrink-0 font-mono text-[11px] text-ink-500">{count} รายการ</span>
    </button>
  );
}

/** ต้นทุนต่อหน่วย — เงินสดไม่มีต้นทุนต่อหน่วยให้ดู (เป็น 1 เสมอโดยนิยาม) */
function UnitCost({ holding }: { holding: Holding }) {
  if (holding.assetType === 'cash') {
    return <span className="text-ink-500">—</span>;
  }
  return (
    <>
      {formatNumber(holding.avgCost)}{' '}
      <span className="text-[11px] text-ink-500">{holding.currency}</span>
    </>
  );
}

const ACTION_CLASS =
  'rounded border border-line-strong px-2.5 py-1.5 text-xs transition whitespace-nowrap';

function RowActions({
  holding,
  onBuy,
  onSell,
  onEdit,
  onDelete,
}: {
  holding: Holding;
  onBuy: () => void;
  onSell: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
      {/*
        เงินสดไม่มีปุ่มซื้อเพิ่ม/ขาย — ต้นทุนต่อหน่วยของเงินสดเป็น 1 เสมอโดยนิยาม
        การถัวเฉลี่ยจึงไม่มีความหมาย ถ้าจะแก้จำนวนเงินให้กด "แก้ไข" ตรง ๆ ชัดเจนกว่า
      */}
      {holding.assetType !== 'cash' && (
        <>
          <button
            type="button"
            onClick={onBuy}
            className={`${ACTION_CLASS} text-up hover:border-up hover:bg-up/10`}
          >
            ซื้อเพิ่ม
          </button>
          <button
            type="button"
            onClick={onSell}
            className={`${ACTION_CLASS} text-ink-200 hover:border-brand hover:text-brand-text`}
          >
            ขาย
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onEdit}
        className={`${ACTION_CLASS} text-ink-200 hover:border-brand hover:text-brand-text`}
      >
        แก้ไข
      </button>
      <button
        type="button"
        onClick={onDelete}
        className={`${ACTION_CLASS} text-down hover:bg-down/10`}
      >
        ลบ
      </button>
    </div>
  );
}
